// T3-B PR 4-bis (2026-05-12):
//   tier プリセット一覧 + 編集画面。SponsorManagement パターン踏襲。
//
// Spec: design/sponsored-motif-spec.md v3 §6.7

import { Button } from '@/components/common/Button';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import TierPresetFormSheet from './components/TierPresetFormSheet';

export default observer(function TierPresetManagementPage() {
    const { t } = useTranslation();
    const {
        tierCatalogStore: { tierPresets, listTierPresets, removeTierPreset },
        modalStore: { showAlertModal }
    } = useStore();

    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
    const [editTarget, setEditTarget] = useState<any | null>(null);

    const fetchList = () => flowResult(listTierPresets({ include_inactive: 1 }));

    useEffect(() => {
        fetchList();
    }, []);

    const handleOpenCreate = () => {
        setSheetMode('create');
        setEditTarget(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (preset: any) => {
        setSheetMode('edit');
        setEditTarget(preset);
        setSheetOpen(true);
    };

    const handleDelete = (preset: any) => {
        showAlertModal({
            title: t('words_title.confirm_delete') || '削除確認',
            size: 'sm',
            content: `「${preset.name}」を削除しますか?\n参照中の motif_template_set があると 409 で拒否されます (= is_active=false 代替推奨)。`,
            onSave: async () => {
                const res = await flowResult(removeTierPreset(preset.id));
                if (res?.ok) {
                    toastify('success', '削除しました');
                    fetchList();
                } else {
                    toastify('error', res?.message || '削除に失敗しました');
                }
            }
        });
    };

    return (
        <div className='layout-card'>
            <div className='flex items-center justify-between mb-4'>
                <div>
                    <h2 className='text-[18px] font-bold'>{t('words_title.tier_preset_management') || 'tier プリセット管理'}</h2>
                    <p className='text-sm text-gray-500 mt-1'>{`登録件数: ${tierPresets.length}`}</p>
                </div>
                <Button variant='active' onClick={handleOpenCreate}>
                    新規追加
                </Button>
            </div>

            {tierPresets.length === 0 ?
                (
                    <div className='py-16 text-center text-gray-500'>登録されている tier プリセットがありません</div>
                ) :
                (
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-gray-200 text-left'>
                                    <th className='py-2 px-3'>ID</th>
                                    <th className='py-2 px-3'>preset_key</th>
                                    <th className='py-2 px-3'>表示名</th>
                                    <th className='py-2 px-3'>tier 数</th>
                                    <th className='py-2 px-3'>active</th>
                                    <th className='py-2 px-3 w-[100px]'>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tierPresets.map(p => (
                                    <tr key={p.id} className='border-b border-gray-100 hover:bg-gray-50'>
                                        <td className='py-2 px-3 text-gray-500'>{p.id}</td>
                                        <td className='py-2 px-3 font-mono text-xs'>{p.preset_key}</td>
                                        <td className='py-2 px-3 font-medium'>{p.name}</td>
                                        <td className='py-2 px-3'>{p.tier_definitions?.length ?? 0}</td>
                                        <td className='py-2 px-3'>{p.is_active ? '✓' : '×'}</td>
                                        <td className='py-2 px-3'>
                                            <div className='flex gap-2'>
                                                <Button variant='outline' size='sm' onClick={() => handleOpenEdit(p)}>
                                                    <Pencil className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleDelete(p)}
                                                    className='text-red-600 hover:text-red-800'
                                                >
                                                    <Trash2 className='w-4 h-4' />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            <TierPresetFormSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                mode={sheetMode}
                preset={editTarget}
                onSuccess={fetchList}
            />
        </div>
    );
});
